import Script from 'next/script';
import {useCallback, useEffect} from 'react';

import {getOid} from '@utils/omicron/getOidSegments';

import {
  useYandexAdsContextSelector,
  YandexAdsContextCommonSelectors,
} from '../YandexAdsContext';

type Props = {
  onLoad?: () => void;
  onError?: () => void;
};

const intervalMs = 100;
const attemptsLimit = 100;

export default function YandexAdScript(props: Props) {
  const {
    activeAdsTypes,
    scriptState,
    setScriptState,
    logTmrEvent,
    emitAdFoxEvent,
  } = useYandexAdsContextSelector(YandexAdsContextCommonSelectors.getFullState);

  const log = useCallback((logEvent: string) => {
    logTmrEvent({
      logEvent,
      params: {user_id: getOid(), platform: activeAdsTypes[0]},
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logAdfox = useCallback(
    ({
      attemptNumber,
      errorType,
    }: {
      attemptNumber: number;
      errorType?: string;
    }) => {
      emitAdFoxEvent({
        eventName: 'AdFoxLoaded',
        payload: {
          attempt_number: `${attemptNumber}`,
          has_error: `${+!!errorType}`,
          ...(errorType && {error_type: errorType}),
        },
      });
    },
    [],
  );

  useEffect(() => {
    log('yan.script.mounted');

    window.yaContextCb = window.yaContextCb || [];

    window.yaContextCb.push(() => {
      log('yan.script.on_initialized');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scriptState !== 'not_initialized') {
      return;
    }
    let attemptNumber = 0;

    const interval = setInterval(() => {
      attemptNumber++;
      if (window.Ya?.adfoxCode && window.yaContextCb) {
        setScriptState('loaded');
        logAdfox({attemptNumber});
      } else if (attemptNumber >= attemptsLimit) {
        setScriptState('failed');
        logAdfox({attemptNumber, errorType: 'attempts_limit_exceeded'});
      }
    }, intervalMs);
    return () => {
      clearInterval(interval);
    };
  }, [scriptState]);

  return (
    <Script
      id="yandex-ads"
      src="https://yandex.ru/ads/system/context.js"
      async
      onLoad={() => {
        log('yan.script.on_load');
        props.onLoad?.();
      }}
      onError={() => {
        log('yan.script.on_error');
        logAdfox({attemptNumber: 0, errorType: 'script_blocked'});
        setScriptState('failed');
        props.onError?.();
      }}
    />
  );
}
